import { ShieldX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { helpPath } from '@/features/help/data/helpCategories'
import { Modal } from '@/shared/components/modal'
import { useLocaleStore } from '@/shared/store/localeStore'

interface Props {
  open: boolean
  /** Tipo de cooldown activo. Cambia el texto del modal. */
  kind: 'join' | 'create'
  /** ISO LocalDateTime futuro hasta cuándo dura el cooldown. */
  until: string
  onClose: () => void
}

/**
 * Modal prominente cuando el usuario intenta una acción bloqueada por un
 * cooldown del sistema de fiabilidad. Sustituye al mensaje pequeño bajo
 * el botón "Unirme" que era apenas visible — el spec original era reactivo
 * al 403 del backend, pero como el FE ya conoce el cooldown propio vía
 * /me/profile, lo abrimos client-side antes de llamar al API.
 */
export function CooldownBlockedModal({ open, kind, until, onClose }: Props) {
  const { t, i18n } = useTranslation()
  const currentLocale = useLocaleStore((s) => s.locale)
  if (!open) return null

  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB'
  const formatted = new Date(until).toLocaleString(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
  })

  const title = t(
    kind === 'join'
      ? 'reliability.cooldownModal.joinTitle'
      : 'reliability.cooldownModal.createTitle',
  )
  // Body sin la fecha interpolada — la fecha se renderiza por separado
  // como bloque azul destacado para que el usuario la vea de un vistazo.
  const body = t(
    kind === 'join' ? 'reliability.cooldownModal.joinBody' : 'reliability.cooldownModal.createBody',
  )

  return (
    <Modal onClose={onClose} labelledBy="cooldown-blocked-modal-title">
      <div className="mb-4 flex items-start gap-3">
        <span className="chip flex size-10 shrink-0 items-center justify-center rounded-md bg-red text-background">
          <ShieldX size={20} strokeWidth={2.5} aria-hidden="true" />
        </span>
        <h3
          id="cooldown-blocked-modal-title"
          className="font-display text-xl font-black leading-tight"
        >
          {title}
        </h3>
      </div>
      <p className="mb-3 text-sm font-medium leading-relaxed text-foreground">{body}</p>
      <p
        className="brutal-sm mb-4 rounded-lg bg-blue px-3 py-2 text-center font-display text-base font-black text-background"
        data-testid="cooldown-until"
      >
        {formatted}
      </p>
      <p className="mb-5 text-center text-sm">
        <Link
          to={helpPath('reliability', currentLocale)}
          onClick={onClose}
          className="font-display font-bold text-foreground underline decoration-2 underline-offset-2 hover:text-red"
        >
          {t('reliability.cooldownModal.learnMore')}
        </Link>
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="brutal-sm brutal-press inline-flex items-center justify-center rounded-lg bg-foreground px-4 py-2 font-display text-sm font-bold text-background"
        >
          {t('reliability.cooldownModal.dismiss')}
        </button>
      </div>
    </Modal>
  )
}
